/**
 * ranzen-wishlist.js
 * Wishlist sin app — localStorage + render en /pages/wishlist
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'ranzen-wishlist-v1';

  /* ── Helpers ──────────────────────────────────────────────── */

  function loadData() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function saveData(arr) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    window.dispatchEvent(new CustomEvent('ranzen:wishlist:updated', { detail: { items: arr } }));
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    const amount = (cents / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return '$\u00a0' + amount;
  }

  /* ── Core API ─────────────────────────────────────────────── */

  const Wishlist = {
    getAll() { return loadData(); },

    has(productId) {
      return loadData().some(p => String(p.id) === String(productId));
    },

    add(product) {
      const items = loadData().filter(p => String(p.id) !== String(product.id));
      items.unshift(product);
      saveData(items);
    },

    remove(productId) {
      saveData(loadData().filter(p => String(p.id) !== String(productId)));
    },

    toggle(product) {
      this.has(product.id) ? this.remove(product.id) : this.add(product);
      return this.has(product.id);
    },

    count() { return loadData().length; }
  };

  /* ── UI — Buttons on product cards ───────────────────────── */

  function updateButton(btn, isActive) {
    const path = btn.querySelector('path');
    if (!path) return;
    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-label',
      isActive
        ? btn.dataset.labelRemove || 'Quitar de favoritos'
        : btn.dataset.labelAdd   || 'Agregar a favoritos'
    );
    if (isActive) {
      path.setAttribute('fill', 'currentColor');
      path.setAttribute('stroke', 'currentColor');
    } else {
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
    }
  }

  function playAddAnimation(btn) {
    btn.classList.remove('rz-wishlist-pop');
    // force reflow
    void btn.offsetWidth;
    btn.classList.add('rz-wishlist-pop');
    btn.addEventListener('animationend', () => btn.classList.remove('rz-wishlist-pop'), { once: true });
  }

  function initButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id = btn.dataset.productId;
      if (!id) return;
      updateButton(btn, Wishlist.has(id));

      if (btn._rzWishlistBound) return;
      btn._rzWishlistBound = true;

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const product = {
          id:               btn.dataset.productId,
          handle:           btn.dataset.productHandle,
          title:            btn.dataset.productTitle,
          price:            parseInt(btn.dataset.productPrice, 10),
          compare_at_price: btn.dataset.productCompare ? parseInt(btn.dataset.productCompare, 10) : null,
          image:            btn.dataset.productImage,
          url:              btn.dataset.productUrl,
          variant_id:       btn.dataset.variantId,
          available:        btn.dataset.available !== 'false'
        };

        const added = Wishlist.toggle(product);
        updateButton(btn, added);
        if (added) playAddAnimation(btn);

        // sync all buttons for the same product
        document.querySelectorAll(`[data-wishlist-btn][data-product-id="${product.id}"]`).forEach(b => {
          if (b !== btn) updateButton(b, added);
        });
      });
    });
  }

  /* ── Wishlist counter badge ───────────────────────────────── */

  function updateCounters() {
    const count = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ── Wishlist Page ────────────────────────────────────────── */

  function renderCard(p) {
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const soldOut = !p.available;

    const imageHtml = p.image
      ? `<img src="${p.image}" alt="${p.title}" class="product-card__img product-card__img--main" width="400" height="457" loading="lazy">`
      : `<div class="product-card__img product-card__img--placeholder"></div>`;

    const priceHtml = onSale
      ? `<del class="product-card__price product-card__price--regular">${formatMoney(p.compare_at_price)}</del>
         <ins class="product-card__price product-card__price--sale">${formatMoney(p.price)}</ins>`
      : `<span class="product-card__price">${formatMoney(p.price)}</span>`;

    const ctaHtml = soldOut
      ? `<a href="${p.url}" class="product-card__btn product-card__btn--read"><span>Leer más</span></a>`
      : `<form action="/cart/add" method="post">
           <input type="hidden" name="id" value="${p.variant_id}">
           <input type="hidden" name="quantity" value="1">
           <button type="submit" class="product-card__btn product-card__btn--add"><span>Añadir Al Carrito</span></button>
         </form>`;

    return `
      <div class="product-card${soldOut ? ' product-card--sold-out' : ''}">
        <div class="product-card__wrapper">
          <div class="product-card__media">
            ${soldOut ? '<div class="product-card__labels"><span class="product-card__label product-card__label--out-of-stock">Agotado</span></div>' : ''}
            <a href="${p.url}" class="product-card__image-link">${imageHtml}</a>
            <div class="product-card__wishlist">
              <button type="button"
                class="product-card__wishlist-btn is-active"
                data-wishlist-btn
                data-product-id="${p.id}"
                data-product-handle="${p.handle}"
                data-product-title="${p.title}"
                data-product-price="${p.price}"
                data-product-compare="${p.compare_at_price || ''}"
                data-product-image="${p.image || ''}"
                data-product-url="${p.url}"
                data-variant-id="${p.variant_id}"
                data-available="${p.available}"
                data-label-add="Agregar a favoritos"
                data-label-remove="Quitar de favoritos"
                aria-label="Quitar de favoritos">
                <svg width="20" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 21C12 21 3 15 3 8.5C3 5.42 5.42 3 8.5 3C10.24 3 11.91 3.88 13 5.27C14.09 3.88 15.76 3 17.5 3C20.58 3 23 5.42 23 8.5C23 15 12 21 12 21Z"
                        fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          <div class="product-card__body">
            <div class="product-card__header">
              <h3 class="product-card__title"><a href="${p.url}">${p.title}</a></h3>
            </div>
            <div class="product-card__price-wrap">${priceHtml}</div>
            <div class="product-card__footer">${ctaHtml}</div>
          </div>
        </div>
      </div>`;
  }

  function renderWishlistPage() {
    const container = document.getElementById('rz-wishlist-page');
    if (!container) return;

    const emptyState = document.getElementById('rz-wishlist-empty');
    const grid      = document.getElementById('rz-wishlist-grid');

    function render() {
      const items = Wishlist.getAll();
      if (items.length === 0) {
        emptyState.style.display = '';
        grid.innerHTML = '';
        grid.style.display = 'none';
      } else {
        emptyState.style.display = 'none';
        grid.style.display = '';
        grid.innerHTML = items.map(renderCard).join('');
        initButtons(); // bind newly rendered buttons
      }
    }

    render();
    window.addEventListener('ranzen:wishlist:updated', render);
  }

  /* ── Boot ─────────────────────────────────────────────────── */

  function boot() {
    initButtons();
    updateCounters();
    renderWishlistPage();

    window.addEventListener('ranzen:wishlist:updated', updateCounters);

    // Re-init when Shopify loads new content (cart drawer, etc.)
    document.addEventListener('shopify:section:load', initButtons);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose globally for debugging
  window.RanzenWishlist = Wishlist;
})();
